<script setup lang="ts">
import { onBeforeUnmount, ref, useId, watch } from 'vue'

/**
 * A small `?` affordance with a hover balloon.
 *
 * Hover alone is not enough: it excludes keyboard and touch users entirely. So the
 * trigger is a real button that also opens on focus and toggles on tap/Enter, and
 * the balloon is wired up with `aria-describedby` so screen readers announce it
 * with the control it explains.
 */
const props = defineProps<{
  /** Accessible name for the trigger, e.g. "What are reserved addresses?". */
  label: string
  /** The balloon's text. Keep it to a sentence or two. */
  text: string
}>()

const id = `infotip-${useId()}`
const pinned = ref(false)
const root = ref<HTMLElement | null>(null)

function onDocumentPointer(event: PointerEvent): void {
  if (root.value && !root.value.contains(event.target as Node)) pinned.value = false
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') pinned.value = false
}

// Listeners only exist while something is pinned open.
watch(pinned, (open) => {
  if (typeof document === 'undefined') return
  if (open) {
    document.addEventListener('pointerdown', onDocumentPointer)
    document.addEventListener('keydown', onKeydown)
  } else {
    document.removeEventListener('pointerdown', onDocumentPointer)
    document.removeEventListener('keydown', onKeydown)
  }
})

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  document.removeEventListener('pointerdown', onDocumentPointer)
  document.removeEventListener('keydown', onKeydown)
})

void props
</script>

<template>
  <span ref="root" class="infotip" :class="{ 'infotip--pinned': pinned }">
    <button
      type="button"
      class="infotip__trigger"
      :aria-label="label"
      :aria-describedby="id"
      :aria-expanded="pinned"
      @click="pinned = !pinned"
    >
      <span aria-hidden="true">?</span>
    </button>
    <span :id="id" class="infotip__bubble" role="tooltip">{{ text }}</span>
  </span>
</template>

<style scoped>
.infotip {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
}

.infotip__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.05rem;
  height: 1.05rem;
  padding: 0;
  border: 1px solid var(--color-border-strong);
  border-radius: 50%;
  background: transparent;
  color: var(--color-text-muted);
  font: inherit;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.infotip__trigger:hover,
.infotip__trigger:focus-visible,
.infotip--pinned .infotip__trigger {
  color: var(--color-accent);
  border-color: var(--color-accent);
}

.infotip__bubble {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  z-index: 20;
  width: max-content;
  max-width: min(17rem, 70vw);
  transform: translateX(-50%);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-raised);
  color: var(--color-text);
  box-shadow: var(--shadow-md);
  font-size: var(--text-sm);
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--transition-fast),
    visibility var(--transition-fast);
}

/* Opens on hover, on keyboard focus, and when tapped. */
.infotip:hover .infotip__bubble,
.infotip__trigger:focus-visible ~ .infotip__bubble,
.infotip--pinned .infotip__bubble {
  opacity: 1;
  visibility: visible;
}

/*
 * Arrow: two stacked triangles, the lower one in the border colour and the upper
 * one in the bubble's fill, so the point keeps the bubble's 1px outline instead of
 * reading as a solid dark wedge.
 */
.infotip__bubble::before,
.infotip__bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  border: 6px solid transparent;
}

.infotip__bubble::before {
  margin-left: -6px;
  border-top-color: var(--color-border);
}

.infotip__bubble::after {
  margin-left: -6px;
  border-top-color: var(--color-surface-raised);
  margin-top: -1px;
}
</style>
